/**
 * LoginActivityTab Component
 *
 * Tab for viewing user login history and activity.
 * Features: login attempt list with filters, statistics cards.
 */

import { useState, useMemo } from 'react';
import { IconHistory, IconRefresh, IconLogin, IconX, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import {
  Card,
  Stack,
  Group,
  Grid,
  Button,
  Table,
  Badge,
  Alert,
  ScrollArea,
  Select,
  Text,
  EmptyState,
} from '../../../components/ui';
import type { LoginActivityTabProps } from './userTabTypes';

export function LoginActivityTab({
  userId,
  loginAttempts,
  onRefresh,
  loading = false,
}: LoginActivityTabProps) {
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'failure'>('all');

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All Attempts' },
    { value: 'success', label: 'Successful Only' },
    { value: 'failure', label: 'Failed Only' },
  ];

  /**
   * Filtered login attempts
   */
  const filteredAttempts = useMemo(() => {
    if (filterStatus === 'all') return loginAttempts;
    if (filterStatus === 'success') return loginAttempts.filter((a) => a.success);
    if (filterStatus === 'failure') return loginAttempts.filter((a) => !a.success);
    return loginAttempts;
  }, [loginAttempts, filterStatus]);

  /**
   * Calculate statistics
   */
  const stats = useMemo(() => {
    const totalLogins = loginAttempts.length;
    const successfulLogins = loginAttempts.filter((a) => a.success).length;
    const failedLogins = loginAttempts.filter((a) => !a.success).length;
    const lastLogin = loginAttempts.find((a) => a.success);

    return {
      total: totalLogins,
      successful: successfulLogins,
      failed: failedLogins,
      lastLogin: lastLogin?.timestamp || null,
      successRate: totalLogins > 0 ? ((successfulLogins / totalLogins) * 100).toFixed(1) : '0',
    };
  }, [loginAttempts]);

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  /**
   * Format relative time
   */
  const formatRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return formatTimestamp(timestamp);
  };

  /**
   * Handle refresh
   */
  const handleRefresh = async () => {
    setError(null);
    try {
      await onRefresh();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to refresh login activity';
      setError(errorMessage);
    }
  };

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {error && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Error"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder p="md">
            <Stack gap="xs">
              <IconLogin size={32} color="gray" />
              <Text size="xl" fw={700}>
                {stats.total}
              </Text>
              <Text size="xs" c="dimmed">
                Total Attempts
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder p="md" style={{ background: 'linear-gradient(135deg, #16A34A, #4ADE80)', color: 'white' }}>
            <Stack gap="xs">
              <IconCheck size={32} />
              <Text size="xl" fw={700}>
                {stats.successful}
              </Text>
              <Text size="xs">
                Successful Logins
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder p="md" style={{ background: 'linear-gradient(135deg, #DC2626, #FB7185)', color: 'white' }}>
            <Stack gap="xs">
              <IconX size={32} />
              <Text size="xl" fw={700}>
                {stats.failed}
              </Text>
              <Text size="xs">
                Failed Attempts
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder p="md">
            <Stack gap="xs">
              <IconHistory size={32} color="gray" />
              <Text size="xs" fw={700}>
                {stats.lastLogin ? formatRelativeTime(stats.lastLogin) : 'Never'}
              </Text>
              <Text size="xs" c="dimmed">
                Last Login
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Login Attempts Card */}
      <Card withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <div>
              <Text size="lg" fw={600}>
                Login History
              </Text>
              <Text size="xs" c="dimmed">
                Recent login attempts for this user
              </Text>
            </div>
            <Group gap="xs">
              <Select
                data={filterOptions}
                value={filterStatus}
                onChange={(value) => setFilterStatus(value as 'all' | 'success' | 'failure')}
                style={{ width: 180 }}
                size="sm"
              />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={loading}
                size="sm"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Login Attempts Table */}
          {filteredAttempts.length > 0 ? (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Timestamp</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>IP Address</Table.Th>
                    <Table.Th>User Agent</Table.Th>
                    <Table.Th>Failure Reason</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredAttempts.map((attempt) => (
                    <Table.Tr key={attempt.id}>
                      <Table.Td>
                        <Text size="sm">{formatTimestamp(attempt.timestamp)}</Text>
                        <Text size="xs" c="dimmed">
                          {formatRelativeTime(attempt.timestamp)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={attempt.success ? 'green' : 'red'} variant="light">
                          {attempt.success ? 'Success' : 'Failed'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {attempt.ipAddress || 'Unknown'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" lineClamp={2} style={{ maxWidth: 300 }}>
                          {attempt.userAgent || 'Unknown'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {attempt.failureReason ? (
                          <Text size="sm" c="red">
                            {attempt.failureReason}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            -
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          ) : (
            <EmptyState
              icon={<IconHistory size={64} />}
              title="No login attempts"
              description={
                filterStatus === 'all'
                  ? 'This user has no recorded login attempts.'
                  : `No ${filterStatus === 'success' ? 'successful' : 'failed'} login attempts found.`
              }
              size="md"
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
